import { createContext, PropsWithChildren } from 'react';

import { useMutation, useQuery } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

import Loading from '../../components/Loading';
import HttpError from '../../services/utilities/HttpError';
import { LocalStorageKeysEnum } from './consts/LocalStorageKeysEnum';
import { getUser, signIn as signInRequest } from './queries';
import { AuthContextType, SignInRequestType, UserRequestType } from './types';
import { UserResponseType } from './types/user-response-type ';

export const AuthContext = createContext<AuthContextType | null>(null);

function AuthProvider({ children }: PropsWithChildren) {
  const { enqueueSnackbar } = useSnackbar();
  const token = localStorage.getItem(LocalStorageKeysEnum.token);

  const { data: user, isLoading: isUserLoading } = useQuery<UserResponseType>(['getUser', token], getUser, {
    enabled: Boolean(token),
  });

  const { mutate, isLoading: isSignInLoading } = useMutation<UserResponseType, HttpError, SignInRequestType>(signInRequest, {
    onSuccess: (user, variables) => {
      const { callback } = variables;
      localStorage.setItem(LocalStorageKeysEnum.token, 'mock_Token');
      if (callback) {
        callback();
      }
    },
    onError: (error: HttpError) => {
      if (error.status === 401) {
        enqueueSnackbar('Username or password is incorrect.', { variant: 'error' });
      }
    },
  });

  const signIn = (request: SignInRequestType) => {
    mutate(request);
  };

  const signOut = (callback?: VoidFunction) => {
    localStorage.removeItem(LocalStorageKeysEnum.token);
    if (callback) {
      callback();
    }
  };

  if ((token && isUserLoading) || isSignInLoading) {
    return <Loading />;
  }

  return <AuthContext.Provider value={{ user: user as UserRequestType, signIn, signOut }}>{children}</AuthContext.Provider>;
}

export default AuthProvider;
